import { WithModal, withModal } from "Components/General/Modal/withModal";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { BcfServerTopicWithModelIdAndDeleted, SubmitTopicRequest } from "../../Stores/BCFStore";
import { BcfCreateIssueForm } from "./BcfCreateIssueForm";
import { BcfEditIssue } from "./BcfEditIssue";
import { ViewpointManager } from "./ViewpointManager";
import { projectsDataStore } from "Stores";
import { messageInstance } from "App";
import { xktViewerStore } from "Components/XKTViewer/Stores/XKTViewerStore";
import { BCFStore } from "Components/XKTViewer/Stores/BCFStore";
import { BcfFilterTopicsForm } from "./BcfFilterTopicsForm";
import { getBimModalConfirmConfig } from "Pages/Project/BIM/utils/getBimModalConfirmConfig";

interface IBcfSidebarContext {
  bcfStore: BCFStore;
  addViewpointToBcfIssue: (viewpoint: API_BcfServerViewpoint) => void;
  cancelAddViewpointMode: () => void;
  showIssue: (topic: API_BcfServerTopic) => void;
  createIssue: (initialData?: SubmitTopicRequest) => void;
  setAddViewpointMode: (formTypeToSet?: "edit" | "create") => void;
  showFilterTopicsForm: () => void;
}

export const BcfSidebarContext = React.createContext<IBcfSidebarContext>(null);

export const BcfSidebarContextProvider = withModal(
  observer(({ children, showModal }: React.PropsWithChildren<WithModal>) => {
    const { t } = useTranslation(I18N_NS.XKT_VIEWER);
    const bcfStore = xktViewerStore.bcfStore;
    const [formType, setFormType] = React.useState<"edit" | "create">(null);
    const formTypeRef = React.useRef(null);
    const [viewpointManager] = React.useState(() => new ViewpointManager());
    // hides/shows all actions and controls in BIM Viewer, and shows Viewpoint buttons - "Add viewpoint" and "Cancel"
    const setAddViewpointMode = React.useCallback((formTypeToSet: "edit" | "create") => {
      setFormType(formTypeToSet);
      formTypeRef.current = formTypeToSet;
      xktViewerStore.sdk.sendMessage({ type: "enableAddViewpointMode", payload: undefined });
    }, []);

    const restorePreviousForm = React.useCallback(
      (formData) => {
        switch (formTypeRef.current) {
          case "create":
            createIssue(formData);
            break;
          case "edit":
            showIssue(formData);
            break;
          default:
            console.warn(`Unsupported "formType" value: ${formType}`);
            break;
        }
        formTypeRef.current = null;
        setFormType(null);
      },
      [formType]
    );

    const addViewpointToBcfIssue = React.useCallback(
      (viewpointRecord: API_BcfServerViewpoint) => {
        let formData;
        if (formTypeRef.current === "create") {
          formData = bcfStore.addViewpointToFormData(viewpointRecord);
          if (!formData) {
            throw new Error("formData should've been set!");
          }
        } else {
          formData = JSON.parse(bcfStore.preservedFormData);
          viewpointManager.create(viewpointRecord);
        }
        restorePreviousForm(formData);
      },
      [formType, bcfStore, viewpointManager, restorePreviousForm]
    );

    const cancelAddViewpointMode = React.useCallback(() => {
      let formData;
      if (bcfStore.preservedFormData) {
        try {
          formData = JSON.parse(bcfStore.preservedFormData);
        } catch (error) {
          console.warn(`cancelAddViewpointMode: Failed to parse JSON`, error.message);
        }
      }
      restorePreviousForm(formData);
    }, [bcfStore, restorePreviousForm]);

    // if restore == true - try to restore form
    const createIssue = React.useCallback(
      (initialData?: SubmitTopicRequest) => {
        showModal(BcfCreateIssueForm, {
          modalProps: {
            footer: null,
            title: t("Create issue"),
            width: "100%",
            style: { maxWidth: 1200 },
            zIndex: 100010,
          },
          componentProps: {
            initialData: initialData,
            onSetAddViewpointMode: () => setAddViewpointMode("create"),
            bcfStore: bcfStore,
            onSubmit: (topic: API_BcfServerTopic) => {
              messageInstance.open({
                type: "success",
                content: t("Issue created!", { ns: I18N_NS.XKT_VIEWER }),
              });
            },
          },
          confirmConfig: getBimModalConfirmConfig(t),
        });
      },
      [showModal, t, bcfStore]
    );

    const showIssue = React.useCallback(
      (topic: BcfServerTopicWithModelIdAndDeleted) => {
        showModal(BcfEditIssue, {
          modalProps: {
            footer: null,
            title: t("Details"),
            width: "100%",
            style: { maxWidth: 1200 },
            zIndex: 1061,
          },
          componentProps: {
            topic: topic,
            bcfStore: bcfStore,
            viewpointManager: viewpointManager,
            onEnterAddViewpointMode: () => setAddViewpointMode("edit"),
            isTopicDisabled: projectsDataStore.isArchived || topic.deleted,
          },
          confirmConfig: getBimModalConfirmConfig(t),
        });
      },
      [showModal, t, bcfStore, viewpointManager]
    );

    const showFilterTopicsForm = React.useCallback(() => {
      showModal(BcfFilterTopicsForm, {
        modalProps: {
          footer: null,
          title: t("Filter issues"),
          width: "100%",
          style: { maxWidth: 800 },
          zIndex: 100010,
        },
        componentProps: {
          bcfStore: bcfStore,
        },
      });
    }, [showModal, t, bcfStore]);

    const contextValue = React.useMemo(
      () => ({
        addViewpointToBcfIssue,
        cancelAddViewpointMode,
        showIssue,
        createIssue,
        setAddViewpointMode,
        showFilterTopicsForm,
        bcfStore,
      }),
      [
        addViewpointToBcfIssue,
        cancelAddViewpointMode,
        showIssue,
        createIssue,
        setAddViewpointMode,
        showFilterTopicsForm,
        bcfStore,
      ]
    );

    React.useEffect(() => {
      return () => viewpointManager.clear();
    }, []);

    return <BcfSidebarContext.Provider value={contextValue}>{children}</BcfSidebarContext.Provider>;
  })
);
